import { useCallback, useMemo, useState } from 'react';

/**
 * useCrewSessionOverrides
 * Wraps the existing useSessionOverrides to support composite keys (sessionId-crewId)
 * while preserving convenience selectors and flags used by Step3Validation.
 */
export default function useCrewSessionOverrides(base) {
  const {
    sessionOverrides,
    saveSessionOverrides,
    clearSessionOverride,
    prefetchOverrides,
  } = base;

  const [optimisticOverrideSessions, setOptimisticOverrideSessions] = useState(new Set());
  const [isSavingOverridesBySession, setIsSavingOverridesBySession] = useState({});

  const setOverrideForCrew = useCallback(
    async (taskId, crewId, sessionId, payload, api) => {
      try {
        setIsSavingOverridesBySession((s) => ({ ...s, [sessionId]: true }));
        const res = await api.saveSessionOverrides(taskId, sessionId, payload);
        if (res?.success) {
          setOptimisticOverrideSessions((prev) => new Set([...prev, sessionId]));
        }
        return res;
      } finally {
        setIsSavingOverridesBySession((s) => ({ ...s, [sessionId]: false }));
      }
    },
    []
  );

  const clearOverrideForCrew = useCallback(
    async (taskId, sessionId, crewId, api) => {
      try {
        setIsSavingOverridesBySession((s) => ({ ...s, [sessionId]: true }));
        // Try crew-specific endpoint when available, otherwise fallback to general clear
        let res;
        if (api.clearSessionOverridesForCrew) {
          res = await api.clearSessionOverridesForCrew(taskId, sessionId, crewId);
        } else {
          res = await api.clearSessionOverrides(taskId, sessionId);
        }
        if (res?.success) {
          setOptimisticOverrideSessions((prev) => {
            const next = new Set(prev);
            next.delete(sessionId);
            return next;
          });
        }
        return res;
      } finally {
        setIsSavingOverridesBySession((s) => ({ ...s, [sessionId]: false }));
      }
    },
    []
  );

  const hasOverridesForSession = useCallback(
    (sessionId) => {
      const src = sessionOverrides || {};
      const keys = Object.keys(src);
      for (const key of keys) {
        if (key.startsWith(`${sessionId}-`)) {
          const mo = src[key]?.member_overrides;
          if (mo && Object.keys(mo).length > 0) return true;
        }
      }
      return optimisticOverrideSessions.has(sessionId);
    },
    [sessionOverrides, optimisticOverrideSessions]
  );

  const hasOverridesForCrew = useCallback(
    (sessionId, crewId) => {
      const compositeKey = `${sessionId}-${crewId}`;
      const entry = sessionOverrides?.[compositeKey] || sessionOverrides?.[String(compositeKey)];
      const mo = entry?.member_overrides;
      const hasReal = mo && typeof mo === 'object' && Object.keys(mo).length > 0;
      return hasReal || optimisticOverrideSessions.has(sessionId);
    },
    [sessionOverrides, optimisticOverrideSessions]
  );

  const getSessionOverrideForCrew = useCallback(
    (sessionId, crewId) => {
      const compositeKey = `${sessionId}-${crewId}`;
      const stringKey = String(compositeKey);
      const override = sessionOverrides?.[compositeKey] || sessionOverrides?.[stringKey];
      if (override && typeof override === 'object') {
        const nested = override[compositeKey] || override[stringKey];
        if (nested && nested.member_overrides && Object.keys(nested.member_overrides).length > 0) {
          return nested;
        }
        if (override.member_overrides && Object.keys(override.member_overrides).length > 0) {
          return override;
        }
      }
      return null;
    },
    [sessionOverrides]
  );

  const normalizedOverridesList = useMemo(() => {
    try {
      const entries = [];
      const seen = new Set();
      const src = sessionOverrides || {};
      Object.entries(src).forEach(([key, value]) => {
        if (!value) return;
        if (key.includes('-')) {
          const [sid, cid] = key.split('-');
          const nested = value?.[`${sid}-${cid}`];
          const ov = nested && typeof nested === 'object' ? nested : value;
          const id = `${String(sid)}-${String(cid)}`;
          if (!seen.has(id)) {
            seen.add(id);
            entries.push({ sessionId: String(sid), crewId: String(cid), override: ov });
          }
          return;
        }
        if (value && typeof value === 'object' && value.crew_id) {
          const sid = key;
          const cid = String(value.crew_id);
          const id = `${String(sid)}-${String(cid)}`;
          if (!seen.has(id)) {
            seen.add(id);
            entries.push({ sessionId: String(sid), crewId: String(cid), override: value });
          }
          return;
        }
        if (value && typeof value === 'object') {
          Object.entries(value)
            .filter(([k]) => k.includes('-'))
            .forEach(([ck, ov]) => {
              const [sid, cid] = ck.split('-');
              const id = `${String(sid)}-${String(cid)}`;
              if (!seen.has(id)) {
                seen.add(id);
                entries.push({ sessionId: String(sid), crewId: String(cid), override: ov });
              }
            });
        }
      });
      return entries;
    } catch (e) {
      return [];
    }
  }, [sessionOverrides]);

  return {
    optimisticOverrideSessions,
    isSavingOverridesBySession,
    setOverrideForCrew,
    clearOverrideForCrew,
    hasOverridesForSession,
    hasOverridesForCrew,
    getSessionOverrideForCrew,
    normalizedOverridesList,
    prefetchOverrides,
  };
}



